import { useEffect, useState } from "react";
import axios from "axios";
import { Eye, Users, Clock, MousePointerClick } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDuration(ms) {
  const s = Math.round((ms || 0) / 1000);
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="border border-[#262626] bg-[#0f0f0f] p-5">
      <div className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-widest text-neutral-500 mb-3">
        <Icon size={13} className="text-[#ff4500]" /> {label}
      </div>
      <div className="font-serif text-4xl font-light text-white">{value}</div>
    </div>
  );
}

function RankList({ title, items, testid, format }) {
  const max = Math.max(1, ...(items || []).map((i) => i.count));
  return (
    <div data-testid={testid} className="border border-[#262626] bg-[#0f0f0f] p-5">
      <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-500 mb-4">{title}</div>
      {!items || items.length === 0 ? (
        <p className="text-neutral-600 text-sm font-mono">No data yet</p>
      ) : (
        <div className="space-y-2.5">
          {items.map((it) => (
            <div key={it.name}>
              <div className="flex items-center justify-between gap-3 text-sm mb-1">
                <span className="text-neutral-300 truncate">{format ? format(it.name) : it.name}</span>
                <span className="font-mono text-xs text-[#ff4500] flex-shrink-0">{it.count}</span>
              </div>
              <div className="h-[3px] bg-[#1a1a1a]">
                <div className="h-full bg-[#ff4500]" style={{ width: `${(it.count / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminAnalytics({ token }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/analytics/summary?days=${days}`, { headers: { "X-Admin-Token": token } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days, token]);

  const daily = data?.daily || [];
  const maxViews = Math.max(1, ...daily.map((d) => d.views));
  const outboundTotal = (data?.outbound || []).reduce((a, b) => a + b.count, 0);

  return (
    <div data-testid="admin-analytics-panel">
      <div className="flex items-center justify-between mb-6">
        <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
          Privacy-friendly · no cookies · IPs never stored
        </p>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`analytics-range-${d}`}
              onClick={() => setDays(d)}
              className={`px-4 py-1.5 font-mono text-[10px] uppercase tracking-widest border ${
                days === d
                  ? "bg-[#ff4500] text-black border-[#ff4500]"
                  : "border-[#262626] text-neutral-400 hover:border-[#ff4500]"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="font-mono text-xs text-neutral-500 py-10">Loading analytics…</div>}

      {!loading && !data && (
        <div className="font-mono text-xs text-red-400 py-10">Could not load analytics.</div>
      )}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <StatCard icon={Eye} label="Page views" value={data.total_views} testid="stat-views" />
            <StatCard icon={Users} label="Unique visitors" value={data.unique_visitors} testid="stat-visitors" />
            <StatCard icon={Clock} label="Avg. engagement" value={fmtDuration(data.avg_engagement_ms)} testid="stat-engagement" />
            <StatCard icon={MousePointerClick} label="Outbound clicks" value={outboundTotal} testid="stat-outbound" />
          </div>

          <div data-testid="analytics-trend" className="border border-[#262626] bg-[#0f0f0f] p-5 mb-6">
            <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-500 mb-4">
              Daily views (bars) & unique visitors (orange markers)
            </div>
            {daily.length === 0 ? (
              <p className="text-neutral-600 text-sm font-mono">No visits in this period yet</p>
            ) : (
              <div className="flex items-end gap-[3px] h-32">
                {daily.map((d) => (
                  <div key={d.day} className="flex-1 flex flex-col items-center justify-end gap-1 group relative">
                    <span className="absolute -top-1 opacity-0 group-hover:opacity-100 transition-opacity font-mono text-[9px] text-white bg-black border border-[#262626] px-1.5 py-0.5 whitespace-nowrap z-10">
                      {d.day} · {d.views} views · {d.visitors} visitors
                    </span>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#ff4500]" title={`${d.visitors} visitors`} />
                    <div
                      className="w-full bg-[#2a2a2a] group-hover:bg-[#ff4500]/60 transition-colors"
                      style={{ height: `${Math.max(4, (d.views / maxViews) * 100)}%` }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            <RankList title="Most viewed pages & projects" items={data.top_pages} testid="list-pages" />
            <RankList title="Traffic sources" items={data.sources} testid="list-sources" />
            <RankList title="Devices" items={data.devices} testid="list-devices" />
            <RankList title="Countries" items={data.countries} testid="list-countries" />
            <RankList title="Cities" items={data.cities} testid="list-cities" />
            <RankList
              title="Outbound link clicks"
              items={data.outbound}
              testid="list-outbound"
              format={(n) => n.replace(/^https?:\/\//, "")}
            />
          </div>

          <p className="font-mono text-[10px] text-neutral-600 mt-6 leading-relaxed">
            What this can tell you: views, visitors per day, where they came from, devices, approximate
            country/city, engagement time and which external links they click. What it cannot: visitors are
            anonymous by design — no names, emails or cross-day returning-visitor identification (that would
            require cookies and a consent banner).
          </p>
        </>
      )}
    </div>
  );
}
